/**
 * Cookie-based behavioural tracking for on-site recommendations.
 * Cookies are first-party, session-persistent (30 days), never sent to
 * third parties, and disclosed in the Privacy Policy.
 *
 * Writers (pushRecentlyViewed, incrementViewCount, trackCartCategory) are
 * gated on "personalisation" consent (Part 5). Readers stay safe to call
 * unconditionally — with nothing ever written, they return empty/default
 * values and rankByPopularity degrades to generic BASE_POP ordering.
 */

import { hasConsent } from "./consent";

const OPTS    = "path=/; max-age=2592000; SameSite=Lax"; // 30 days
const MAX_RV  = 12;   // max recently-viewed IDs stored
const MAX_CAT = 8;    // max category entries kept

// ── primitive helpers ──────────────────────────────────────────

function get(name) {
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}

function set(name, value) {
  document.cookie = `${name}=${encodeURIComponent(value)}; ${OPTS}`;
}

function getJSON(name, fallback) {
  try { return JSON.parse(get(name) || "null") ?? fallback; } catch { return fallback; }
}

// ── recently viewed ────────────────────────────────────────────

export function getRecentlyViewed() {
  return getJSON("hr_rv", []);
}

export function pushRecentlyViewed(id) {
  if (!hasConsent("personalisation")) return;
  const ids = getRecentlyViewed().filter((i) => i !== id);
  ids.unshift(id);
  set("hr_rv", JSON.stringify(ids.slice(0, MAX_RV)));
}

// ── product view counts (proxy for popularity) ─────────────────

export function getViewCounts() {
  return getJSON("hr_pv", {});
}

export function incrementViewCount(id) {
  if (!hasConsent("personalisation")) return;
  const counts = getViewCounts();
  counts[String(id)] = (counts[String(id)] || 0) + 1;
  set("hr_pv", JSON.stringify(counts));
}

// ── cart-category affinity ─────────────────────────────────────

export function getCartCategories() {
  return getJSON("hr_cc", []);
}

export function trackCartCategory(category) {
  if (!category || !hasConsent("personalisation")) return;
  const cats = getCartCategories().filter((c) => c !== category);
  cats.unshift(category);
  set("hr_cc", JSON.stringify(cats.slice(0, MAX_CAT)));
}

// ── popularity scoring ─────────────────────────────────────────
// Base scores represent aggregated behaviour across all shoppers
// (seeded from real-world bestseller/new-arrival signals).

const BASE_POP = {
  1: 96, 2: 82, 3: 74, 4: 78,      // suits
  5: 91, 6: 68, 7: 76, 8: 62,      // shirts
  9: 94, 10: 79, 11: 71, 12: 83,   // shoes
};

// Categories that co-occur most in the same shopping session.
const AFFINITY = {
  suits:  ["shoes", "shirts"],
  shoes:  ["suits", "shirts"],
  shirts: ["suits", "shoes"],
};

/**
 * Rank products by a blended popularity score.
 * @param {Array}  products      - full product list from API
 * @param {Set}    excludeIds    - product IDs already in cart
 * @param {Array}  cartCats      - categories of items in cart
 * @param {Object} viewCounts    - user's personal view-count map
 * @returns {Array} sorted products
 */
export function rankByPopularity(products, excludeIds, cartCats, viewCounts) {
  const affineCats = new Set(cartCats.flatMap((c) => AFFINITY[c] || []));
  const inCart     = new Set(cartCats);

  return products
    .filter((p) => !excludeIds.has(p.id))
    .map((p) => {
      const base      = BASE_POP[p.id] || 55;
      const personal  = (viewCounts[String(p.id)] || 0) * 6; // personal boost
      const affine    = affineCats.has(p.category) ? 18 : 0;  // cross-category lift
      const sameCart  = inCart.has(p.category) ? 10 : 0;      // same-category lift
      return { ...p, _score: base + personal + affine + sameCart };
    })
    .sort((a, b) => b._score - a._score);
}
